import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { Option } from '@repo/toolkit/types'
import type { TFunction } from 'i18next'

import { AffectedStaffDailyReportInput } from '../../lib/types/models/affectedStaff/affected-staff-daily-report'
import { AffectedStaffEndOfStayReportInput } from '../../lib/types/models/affectedStaff/affected-staff-end-of-stay-report'
import { Country } from '../../lib/types/models/country/country'
import { Formula } from '../../lib/types/models/formula/formula'
import { PeriodTypes } from '../../lib/types/models/period/period'
import { Staff } from '../../lib/types/models/staff/staff'
import { StaffInput } from '../../lib/types/models/staff/staff-input'
import { StaffPayScale } from '../../lib/types/models/staff-pay-scale/staff-pay-cale'

export const yearsFilterOptions: Option<number>[] = [
  ...Array.from({ length: 20 }, (_, i) => {
    const year = new Date().getFullYear() - i
    return { label: year.toString(), value: year }
  })
]

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export const mapStaffToInputPersonalSpace = (staff: Staff): StaffInput => {
  const { id: _id, ...rest } = staff

  return {
    ...rest,
    hobbyIds: rest.hobbies?.map(({ id }) => id),
    email: rest.user?.email,
    diplomas: rest.diplomas?.map(({ staff: _staff, staffId: _staffId, ...rest }) => ({ ...rest })),
    applicationId: rest.application?.id
  }
}

export type DailySalarySliceType = 'first' | 'second' | 'third'

export const dailySalarySliceTypeMapping: Record<DailySalarySliceType, string> = {
  first: '1er séjour',
  second: '2 à 4 séjours',
  third: '5 séjours et plus'
}

export type StaffPayScaleDetail = {
  formulaId: number
  countries: Country[]
  formula: Formula
  dailySalarySliceType: DailySalarySliceType
  dailySalarySlice: number
  dailySalarySlicex8: number
  dailySalarySlicex13: number
  dailySalarySlicex14: number
}

export const mapStaffPayScaleToTableData = (
  staffPayScale: StaffPayScale
): StaffPayScaleDetail[] => {
  const result: StaffPayScaleDetail[] = []

  for (const formula of staffPayScale.formulas) {
    const commonData = {
      formulaId: formula.id,
      countries: staffPayScale.countries,
      formula
    }

    if (staffPayScale.dailySalaryFromFirstSlice)
      result.push({
        ...commonData,
        dailySalarySliceType: 'first',
        dailySalarySlice: staffPayScale.dailySalaryFromFirstSlice,
        // 8-day stays include 1 paid leave day, hence 9 paid days
        dailySalarySlicex8: staffPayScale.dailySalaryFromFirstSlice * 9,
        dailySalarySlicex13: staffPayScale.dailySalaryFromFirstSlice * 13,
        dailySalarySlicex14: staffPayScale.dailySalaryFromFirstSlice * 14
      })

    if (staffPayScale.dailySalaryFromSecondSlice)
      result.push({
        ...commonData,
        dailySalarySliceType: 'second',
        dailySalarySlice: staffPayScale.dailySalaryFromSecondSlice,
        // 8-day stays include 1 paid leave day, hence 9 paid days
        dailySalarySlicex8: staffPayScale.dailySalaryFromSecondSlice * 9,
        dailySalarySlicex13: staffPayScale.dailySalaryFromSecondSlice * 13,
        dailySalarySlicex14: staffPayScale.dailySalaryFromSecondSlice * 14
      })
  }

  return result
}

export const mapEndOfStayQuestions = (
  t: TFunction<AvailableLanguages, undefined>
): Record<keyof AffectedStaffEndOfStayReportInput, string | string[]> => ({
  residenceFacilities: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.residenceFacilities'
  ),
  managementTeam: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.managementTeam'
  ),
  peersInvolvement: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.peersInvolvement'
  ),
  excursionsFeedback: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.excursionsFeedback'
  ),
  activitiesEvenings: [
    t(
      'staff.personal.space.my.journeys.details.end.of.stay.questions.activitiesEvenings.professors'
    ),
    t(
      'staff.personal.space.my.journeys.details.end.of.stay.questions.activitiesEvenings.animators'
    )
  ],
  classroomEquipment: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.classroomEquipment'
  ),
  foodQuality: t('staff.personal.space.my.journeys.details.end.of.stay.questions.foodQuality'),
  residenceImprovements: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.residenceImprovements'
  ),
  additionalRemarks: t(
    'staff.personal.space.my.journeys.details.end.of.stay.questions.additionalRemarks'
  )
})

export const mapDailyReportIssues: Record<
  keyof Omit<AffectedStaffDailyReportInput, 'date' | 'previousIssuesProgress' | 'incidentDetails'>,
  string | string[]
> = {
  disciplineIssue: 'Discipline',
  healthIssue: 'Santé',
  drugsIssue: 'Drogue',
  accidentIssue: 'Accident',
  damageIssue: 'Dégradation',
  theftIssue: 'Vol',
  conductIssue: 'Mœurs',
  violenceIssue: 'Violence',
  moralHarassmentIssue: 'Harcèlement moral',
  sexualHarassmentIssue: 'Harcèlement sexuel',
  teamIssue: "Problème au sein de l'équipe",
  otherIssue: 'Autres'
}

export const updateStaffDataMapping = (
  { picture: _picture, diplomas, idFile, criminalRecord, paySlip, ...values }: StaffInput,
  canModify?: boolean
) => {
  // Normalize diplomas: keep status, and ensure file is null when absent
  const diplomasCheckFile = diplomas.map((diploma) => {
    const { status, file, ...rest } = diploma
    return { status, file: file ?? null, ...rest }
  })

  // Base payload built from form values
  const basePayload = {
    ...values,
    diplomas: diplomasCheckFile,
    idFile: idFile ?? null,
    criminalRecord: criminalRecord ?? null,
    paySlip: paySlip ?? null
  }

  // Make a mutable, untyped copy so we can remove keys at runtime without
  // using `any`. We'll cast back to the expected update type at the call site.
  const payloadRecord: Record<string, unknown> = { ...basePayload }

  // When the staff can no longer modify identity data, keep disabled form values out of the request.
  if (!canModify) {
    const fieldsToRemove = [
      'idFile',
      'phoneNumber',
      'idDeliveredAt',
      'idValidUntil',
      'idDeliveredBy',
      'idNumber',
      'idType'
    ]
    for (const f of fieldsToRemove) delete payloadRecord[f]
  }

  return payloadRecord
}

export const getPeriodsDates = (periods: PeriodTypes.Light[]): Date[] =>
  periods
    ?.map((period) => ({
      startDate: DateFormatter.toDate(period.startDate),
      endDate: DateFormatter.toDate(period.endDate)
    }))
    .map((period) => {
      const dates: Date[] = []
      const currentDate = new Date(period.startDate)
      while (currentDate <= period.endDate) {
        dates.push(new Date(currentDate))
        currentDate.setDate(currentDate.getDate() + 1)
      }
      return dates
    })
    .flat()
